// Eigenbau: Bereichs-Paginierung mit Seiten (5/10/20/50 Einträge); Tremor hat
// keinen Paginations-Baustein im Bestand, deshalb eine eigene kompakte Leiste.
import { useState, type ReactNode } from "react";
import { Button } from "@/components/Button";
import { useGemerkteAuswahl } from "@/lib/gemerkte-auswahl";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/Select";

export const PAGINIERUNGS_GROESSEN = [5, 10, 20, 50] as const;
export type PaginierungsGroesse = (typeof PAGINIERUNGS_GROESSEN)[number];

/**
 * Kapselt einen Bereich mit Daten und rendert nur die sichtbare Seite.
 * Die Leiste erscheint, sobald etwas verborgen ist.
 *
 * **Die gewählte Größe bleibt stehen.** Sie lag in `useState` und war nach
 * jeder Neuladung wieder fünf — auf einer Seite mit fünf solchen Leisten also
 * fünfmal von vorn. Sie gehört zur Person, nicht zur Adresse, und liegt
 * deshalb im Webspeicher unter `merkschluessel` (siehe `gemerkte-auswahl`).
 * Der Schlüssel ist Pflicht: Zwei Leisten ohne eigenen Schlüssel würden sich
 * gegenseitig überschreiben, und eine Leiste ohne jeden wäre die einzige, die
 * vergisst.
 */
export function PaginierterBereich<T>({
  daten,
  standard = 5,
  merkschluessel,
  children,
}: {
  daten: T[];
  standard?: PaginierungsGroesse;
  /** Eindeutig je Leiste, etwa `lieferung:merge-requests`. */
  merkschluessel: string;
  children: (sichtbar: T[]) => ReactNode;
}) {
  const [seitenGroesse, setzeSeitenGroesse] = useGemerkteAuswahl<
    PaginierungsGroesse
  >(`paginierung:${merkschluessel}`, PAGINIERUNGS_GROESSEN, standard);
  const [seite, setzeSeite] = useState(1);

  const seiten = Math.max(1, Math.ceil(daten.length / seitenGroesse));
  const aktuelleSeite = Math.min(seite, seiten);
  const start = (aktuelleSeite - 1) * seitenGroesse;
  const sichtbar = daten.slice(start, start + seitenGroesse);
  const ende = Math.min(start + seitenGroesse, daten.length);
  /*
   * Nicht `daten.length > standard`: Ist die gemerkte Größe kleiner als der
   * Standard, verbirgt die Leiste Einträge und wäre selbst unsichtbar — man
   * säße vor einer gekürzten Liste ohne Bedienung.
   */
  const zeigeLeiste = daten.length > Math.min(standard, seitenGroesse);

  return (
    <div className="space-y-3">
      {children(sichtbar)}
      {zeigeLeiste ? (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-gray-50/80 px-3 py-2 text-xs text-gray-500 dark:bg-gray-900/70">
          <label className="flex items-center gap-1.5">
            Einträge pro Seite
            <Select
              value={String(seitenGroesse)}
              onValueChange={(wert) => {
                setzeSeitenGroesse(Number(wert) as PaginierungsGroesse);
                setzeSeite(1);
              }}
            >
              <SelectTrigger
                aria-label="Einträge pro Seite"
                className="h-7 w-16 px-2 text-xs"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGINIERUNGS_GROESSEN.map((groesse) => (
                  <SelectItem key={groesse} value={String(groesse)}>
                    {groesse}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <div className="flex items-center gap-2">
            <span>
              {start + 1}–{ende} von {daten.length}
            </span>
            <Button
              variant="secondary"
              className="h-7 px-2 text-xs"
              disabled={aktuelleSeite <= 1}
              onClick={() => setzeSeite(aktuelleSeite - 1)}
            >
              ←
            </Button>
            <Button
              variant="secondary"
              className="h-7 px-2 text-xs"
              disabled={aktuelleSeite >= seiten}
              onClick={() => setzeSeite(aktuelleSeite + 1)}
            >
              →
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
